"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.selectCurrentQuestionBankWithSubscriptions = selectCurrentQuestionBankWithSubscriptions;
exports.selectQuestionBankWithSubscriptions = void 0;
exports.selectQuestionBanksWithSubscriptions = selectQuestionBanksWithSubscriptions;
var _luxon = require("luxon");
var _reselect = require("../helpers/reselect.js");
var _dateTime = require("../helpers/dateTime.js");
var _subscription = require("../helpers/subscription.js");
var _subscriptions = require("./subscriptions.js");
/* eslint-disable camelcase */

// selectSubscriptionStates is a cache-busting selector which returns all the subscription states for a given QB as a comparable string.
// This busts the cache whenever the time-based status of any subscriptions for the given QB changes.
var selectSubscriptionStates = function selectSubscriptionStates(state, questionBankId) {
  return (0, _subscriptions.selectSubscriptionsForCurrentUser)(state).filter(function (sub) {
    return sub.get('question_bank_id') === questionBankId;
  }).map(_subscription.subscriptionState).join('');
};
var selectQuestionBankWithSubscriptions = exports.selectQuestionBankWithSubscriptions = (0, _reselect.createMultiSelector)([function (state, questionBankId) {
  return state.questionBanks.get(questionBankId);
}, _subscriptions.selectSubscriptionsForCurrentUser, selectSubscriptionStates], function (questionBank, userSubscriptions) {
  var _questionBank$get;
  if (!questionBank) return undefined;
  var subscriptions = (0, _subscription.subscriptionsWithStates)(userSubscriptions.filter(function (subscription) {
    return subscription.get('question_bank_id') === questionBank.get('id');
  })).sortBy(function (subscription) {
    return subscription.get('infinite') ? 1 : (subscription.get('active') ? -2 : -1) * subscription.get('expiry_date');
  });
  if (!subscriptions) return undefined;
  var hasSubscriptionWithPremiumFeatures = !!subscriptions.find(function (subscription) {
    return subscription.get('has_premium_features') && !subscription.get('trial') && !subscription.get('expired');
  });
  var anyDelayedSubscriptions = !!subscriptions.find(function (subscription) {
    return subscription.get('delayed');
  });
  var anyActiveNonTrialSubscriptions = !!subscriptions.find(function (subscription) {
    return subscription.get('active') && !subscription.get('trial');
  });
  var hasAssociatedStudentPurchaseSubscription = !!subscriptions.find(function (subscription) {
    return subscription.get('organization_id') && subscription.get('product_id');
  });
  var infinite = !!subscriptions.find(function (subscription) {
    return subscription.get('active') && subscription.get('infinite');
  });
  var hasPracticeExam = ((_questionBank$get = questionBank.get('practice_exam_template_ids')) === null || _questionBank$get === void 0 ? void 0 : _questionBank$get.size) > 0;
  var trial = subscriptions.size > 0 && !subscriptions.find(function (subscription) {
    return !subscription.get('trial');
  });
  var anyActiveTrialSubscriptions = !!subscriptions.find(function (subscription) {
    return subscription.get('active') && subscription.get('trial');
  });
  var active = anyActiveNonTrialSubscriptions || trial && anyActiveTrialSubscriptions;
  var delayed = !active && anyDelayedSubscriptions;
  var expired = !active && !delayed && !!subscriptions.find(function (subscription) {
    return subscription.get('expired');
  });
  var hasBoardExamInterfaceOptions = ['psi', 'fred', 'pearson_vue'].some(function (name) {
    var _questionBank$get2;
    return ((_questionBank$get2 = questionBank.get("".concat(name, "_board_exam_interface_options"))) === null || _questionBank$get2 === void 0 ? void 0 : _questionBank$get2.size) > 0;
  });
  var hideCme = !!subscriptions.find(function (subscription) {
    return subscription.get('hide_cme');
  });
  var organizationRestricted = !!subscriptions.find(function (subscription) {
    return subscription.get('organization_restricted');
  });
  var expiredOrganizational = !!subscriptions.find(function (subscription) {
    return subscription.get('expired') && subscription.get('organizational');
  });
  var showCme = questionBank.get('has_cme') && !hideCme && !organizationRestricted;
  var organizational = !!subscriptions.find(function (subscription) {
    return subscription.get('active') && subscription.get('organizational');
  });
  var activationDate = !delayed ? 0 : subscriptions.filter(function (subscription) {
    return subscription.get('delayed');
  }).map(function (subscription) {
    return subscription.get('activation_date');
  }).min();
  var expiryDate = function () {
    if (trial) {
      if (infinite) return 0;
      return subscriptions.filter(function (subscription) {
        return subscription.get('active') || subscription.get('expired');
      }).map(function (subscription) {
        return subscription.get('expiry_date');
      }).max();
    }
    var questionBankSubscriptions = subscriptions.filter(function (subscription) {
      return !subscription.get('trial') && (subscription.get('active') || subscription.get('expired') || subscription.get('delayed'));
    });
    if (questionBankSubscriptions.size === 0) return 0;
    return questionBankSubscriptions.map(function (subscription) {
      return subscription.get('expiry_date');
    }).max();
  }();
  var daysLeft = function () {
    if (infinite && trial) return Infinity;
    return Math.floor(_luxon.DateTime.fromMillis(expiryDate).startOf('day').diff((0, _dateTime.getServerDateTime)().startOf('day'), ['days']).toObject().days);
  }();
  return questionBank.merge({
    hasPracticeExam: hasPracticeExam,
    hasBoardExamInterfaceOptions: hasBoardExamInterfaceOptions,
    hasAssociatedStudentPurchaseSubscription: hasAssociatedStudentPurchaseSubscription,
    hasSubscriptionWithPremiumFeatures: hasSubscriptionWithPremiumFeatures,
    anyDelayedSubscriptions: anyDelayedSubscriptions,
    anyActiveTrialSubscriptions: anyActiveTrialSubscriptions,
    anyActiveNonTrialSubscriptions: anyActiveNonTrialSubscriptions,
    active: active,
    expired: expired,
    delayed: delayed,
    trial: trial,
    infinite: infinite,
    daysLeft: daysLeft,
    hideCme: hideCme,
    showCme: showCme,
    organizational: organizational,
    expiredOrganizational: expiredOrganizational,
    organizationRestricted: organizationRestricted,
    expiryDate: expiryDate,
    activationDate: activationDate,
    subscriptions: subscriptions
  });
});
function selectCurrentQuestionBankWithSubscriptions(state) {
  return selectQuestionBankWithSubscriptions(state, state.session.get('selected_question_bank_id'));
}

// This selector always causes a re-render because it creates a new object each time it is used.
// It is recommended to avoid using it and instead render an array of Components which each use the singular selector.
function selectQuestionBanksWithSubscriptions(state) {
  return state.questionBanks.map(function (questionBank, questionBankId) {
    return selectQuestionBankWithSubscriptions(state, questionBankId);
  });
}